<script setup lang="ts">
export interface UserPanelProps {
  user: any
}
import { computed, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useSubjektNav } from '/@src/composable/useSubjekt'

const CLASS_ROOT = 'idsk-header-web'
const { t } = useI18n()
const route = useRoute()

const props = withDefaults(defineProps<UserPanelProps>(), {})
const emit = defineEmits(['logout'])
const active = ref<boolean>(false)

const items = ref<any[]>([])
watchEffect(() => {
  items.value = useSubjektNav(t, props.user, route.path)
})

const subjektNazov = computed(() => {
  return props.user?.subjekt?.nazov ?? props.user?.subjekt?.menoUplne
})
const vZastupeni = computed(() => {
  if (props.user?.subjekt?.osobaId === props.user?.pouzivatel?.osoba?.osobaId) {
    return null
  }
  return props.user?.pouzivatel?.osoba?.menoUplne
})
const handleToggle = () => {
  active.value = !active.value
}
</script>

<template>
  <div class="user-panel">
    <div class="user-panel__content">
      <img src="/user-icon.svg" alt="" loading="eager" />
      <div class="user-panel__data" @keyup.escape="handleToggle">
        <div
          :class="[
            `${CLASS_ROOT}__brand-language`,
            {
              [`${CLASS_ROOT}__brand-language--active`]: active,
            },
          ]"
        >
          <button
            :class="`${CLASS_ROOT}__brand-language-button`"
            :aria-label="
              active ? 'Skryť používateľské menu' : 'Rozbaliť používateľské menu'
            "
            :aria-expanded="active"
            data-test-id="button-pouzivatel"
            @focusout="active = false"
            @click="handleToggle"
          >
            {{ subjektNazov }}
            <span :class="`${CLASS_ROOT}__link-arrow`"></span>
          </button>
          <ul :class="`${CLASS_ROOT}__brand-language-list`">
            <li
              v-for="item in items"
              :key="item.title"
              :class="`${CLASS_ROOT}__brand-language-list-item`"
            >
              <router-link v-slot="{ href, navigate }" :to="item.href" custom>
                <IDSKLink
                  :class="[
                    `${CLASS_ROOT}__brand-language-list-item-link`,
                    {
                      [`${CLASS_ROOT}__brand-language-list-item-link--selected`]:
                        item.isActive,
                      [`${CLASS_ROOT}__brand-language-list-item-link--divider`]:
                        item.hasDivider,
                    },
                  ]"
                  :data-test-id="item.dataTestId"
                  :title="item.title"
                  :href="href"
                  @mousedown.stop.prevent=""
                  @click="
                    (e:any) => {
                      handleToggle()
                      navigate(e)
                    }
                  "
                >
                  {{ item.title }}
                </IDSKLink>
              </router-link>
            </li>
          </ul>
        </div>
        <div v-if="vZastupeni" class="govuk-body-s mb-0">
          {{ `${t('globalne.vZastupeni')} ${vZastupeni}` }}
        </div>
      </div>
    </div>
    <div class="user-panel__actions">
      <IDSKLink href="#" data-test-id="user-panel-odhlasit-sa" @click="emit('logout')">
        {{ t('pouzivatel.odhlasenie') }}
      </IDSKLink>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.user-panel {
  &__content {
    display: flex;
    gap: 5px;
    flex-wrap: nowrap;
    align-items: center;
    margin-bottom: 15px;
  }

  &__data {
    display: flex;
    flex-direction: column;
    align-items: start;
  }
  &__actions {
    margin-left: 35px;
  }
}
.idsk-header-web {
  &__brand-language-list-item > a {
    text-decoration: none;
  }
  &__brand-language {
    width: 100%;
    text-align: left;
    padding: 0;
    position: relative;
  }
  &__brand-language-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 200px;
    width: 100%;
    text-align: left;
    font-weight: bold;
    color: var(--idsk-color-black);
    margin: 0;
    padding: 0;
  }
  &__link-arrow {
    background-color: var(--idsk-color-black);
  }
  &__brand-language-list {
    border-radius: 0 0 5px 5px;
    width: 100%;
  }
  &__brand-language-list-item-link--selected {
    text-decoration: none;
  }
  &__brand-language-list-item-link--divider {
    border-bottom: 1px solid var(--idsk-color-mid-grey);
  }
}
.user-panel__actions {
  @media (max-width: 767px) {
    margin-bottom: 32px;
  }
}
</style>
